import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  ExternalLink,
  Filter,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  adminInputClass,
  filterPillClass,
  filterPillActiveClass,
  StatusPill,
  TableFilterCard,
  TableSkeleton,
} from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingEmpty,
  AccountingError,
} from '@/components/admin/accounting/AccountingShell';
import TransactionDialog from '@/components/admin/accounting/TransactionDialog';
import CategoryPicker from '@/components/admin/accounting/CategoryPicker';
import ConfirmDeleteDialog from '@/components/admin/accounting/ConfirmDeleteDialog';
import BulkDeleteDialog, { BulkDeleteBar } from '@/components/admin/accounting/BulkDeleteDialog';
import useRowSelection from '@/hooks/useRowSelection';
import {
  accountingError,
  activeFilterCount,
  entryModeMeta,
  filtersFromSearch,
  searchFromFilters,
} from '@/lib/accounting';
import {
  createAccountingLedgerAccount,
  createAccountingTransaction,
  deleteAccountingTransaction,
  getAccountingLedgerAccounts,
  getAccountingPayees,
  getAccountingTaxes,
  getAccountingTransactions,
  reviewAccountingTransaction,
  updateAccountingTransaction,
} from '@/lib/api';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS = {
  account: '',
  type: 'all',
  category: '',
  review: 'all',
  source: 'all',
  from: '',
  to: '',
  q: '',
  sort: 'date_desc',
  page: '1',
};

// The tab-like account selector and the sort order are not filters — they are
// which list you are looking at and in what order — so neither is counted.
const COUNTED_FILTERS = {
  type: 'all',
  category: '',
  review: 'all',
  source: 'all',
  from: '',
  to: '',
  q: '',
};

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Largest amount' },
  { value: 'amount_asc', label: 'Smallest amount' },
  { value: 'description_asc', label: 'Description A–Z' },
];

/**
 * Accounting → Transactions.
 *
 * The central list of money movements, and deliberately not a bookkeeping page
 * sitting on its own data. Most of what is on screen was recorded elsewhere in
 * the portal — a student's tuition payment, a commission cheque, a bill
 * payment — and is *read* here rather than re-entered, which is why the list
 * is useful before anybody types anything into it.
 *
 * That has a visible consequence, and the page is built around making it
 * legible rather than hiding it: a row that came from another workflow shows
 * where it came from, cannot have its amount or date changed here, and cannot
 * be deleted here. What it *can* have is the bookkeeping — an account, a
 * category, a note, a review mark — which is the part this section owns.
 *
 * Bank connection and reconciliation are a later phase. There is no toggle for
 * either, because a control that does not reconcile anything is worse than no
 * control at all.
 */
export default function AccountingTransactions() {
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => filtersFromSearch(location.search, DEFAULT_FILTERS));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [chart, setChart] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [payees, setPayees] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newDirection, setNewDirection] = useState('withdrawal');
  const [busyRef, setBusyRef] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const filterCount = activeFilterCount(filters, COUNTED_FILTERS);

  // Deleting a projected row deletes the record it stands for, so those rows
  // are selectable too. `delete_effect.allowed` is the server's own answer —
  // it knows, for instance, whether a commission batch has a live cheque
  // against it, which nothing in the browser does.
  const selection = useRowSelection(rows, {
    keyOf: (row) => row.ref,
    canSelect: (row) => row.delete_effect?.allowed,
  });

  useEffect(() => {
    const search = searchFromFilters(filters, DEFAULT_FILTERS);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
  }, [filters, location.pathname, location.search, navigate]);

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const { data } = await getAccountingTransactions({
          account: filters.account || undefined,
          type: filters.type === 'all' ? undefined : filters.type,
          category: filters.category || undefined,
          review: filters.review === 'all' ? undefined : filters.review,
          source: filters.source === 'all' ? undefined : filters.source,
          from: filters.from || undefined,
          to: filters.to || undefined,
          q: filters.q || undefined,
          sort: filters.sort,
          page: Number(filters.page) || 1,
        });
        setRows(data?.items || []);
        setSummary(data?.summary || null);
        setPageInfo(data?.page_info || null);
        setPermissions(data?.permissions || {});
      } catch (err) {
        setError(accountingError(err, 'Transactions could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  const loadChart = useCallback(async () => {
    try {
      const [accounts, taxList, payeeList] = await Promise.all([
        getAccountingLedgerAccounts(),
        getAccountingTaxes(),
        getAccountingPayees(),
      ]);
      const items = accounts.data?.items || [];
      setChart({
        groups: accounts.data?.groups || [],
        payment_accounts: accounts.data?.payment_accounts || [],
        account_types: accounts.data?.account_types || [],
        byId: Object.fromEntries(items.map((account) => [account.id, account])),
      });
      setTaxes(taxList.data?.items || []);
      setPayees(payeeList.data?.items || []);
    } catch {
      // The pickers degrade to empty; the list keeps its own error state.
    }
  }, []);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch, page: '1' }));

  const categoryOptions = useMemo(() => {
    if (!chart) return [];
    return (chart.groups || []).flatMap((group) =>
      group.accounts.map((account) => ({ ...account, group: group.label }))
    );
  }, [chart]);

  const openCreate = (direction) => {
    setNewDirection(direction);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const submitDialog = async (payload) => {
    if (editing) await updateAccountingTransaction(editing.ref, payload);
    else await createAccountingTransaction(payload);
    toast.success(editing ? 'Transaction updated.' : 'Transaction added.');
    await load({ quiet: true });
  };

  const createCategory = async (payload) => {
    const { data } = await createAccountingLedgerAccount(payload);
    await loadChart();
    toast.success(`${data.name} added.`);
    return data;
  };

  /**
   * Re-file a row from the table, without opening anything.
   *
   * This is the common case by a distance: the category is the one thing a
   * projected row usually needs, and making somebody open a dialog, change one
   * select and save was three interactions for one decision. It is only
   * offered where it is unambiguous — a row already split across several
   * categories cannot be collapsed into one by a single click, so that one
   * still goes through the dialog.
   *
   * The tax flags on the existing line are carried over rather than dropped:
   * changing what something is filed under is not a statement about whether it
   * included GST.
   */
  const quickCategorise = async (row, categoryId) => {
    if (!categoryId || categoryId === row.splits?.[0]?.category_id) return;
    setBusyRef(row.ref);
    try {
      await updateAccountingTransaction(row.ref, {
        splits: [
          {
            category_id: categoryId,
            amount: row.amount_cents / 100,
            description: row.splits?.[0]?.description || '',
            tax_ids: row.splits?.[0]?.tax_ids || [],
          },
        ],
      });
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That category could not be applied.'));
    } finally {
      setBusyRef('');
    }
  };

  const toggleReview = async (row) => {
    setBusyRef(row.ref);
    try {
      await reviewAccountingTransaction(row.ref, !row.reviewed);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That could not be marked as reviewed.'));
    } finally {
      setBusyRef('');
    }
  };

  const confirmDelete = async () => {
    setBusyRef(deleteFor.ref);
    try {
      await deleteAccountingTransaction(deleteFor.ref);
      toast.success('Transaction deleted.');
      setDeleteFor(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That transaction could not be deleted.'));
    } finally {
      setBusyRef('');
    }
  };

  const canCreate = permissions.create_transaction;
  const canEdit = permissions.edit_transaction;
  const canReview = permissions.review_transaction;
  const canDelete = permissions.delete_transaction;

  const actions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => load({ quiet: true })}
        disabled={refreshing}
      >
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {canCreate && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              <Plus className="mr-2 size-4" />
              Add transaction
              <ChevronDown className="ml-2 size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => openCreate('deposit')}>
              <ArrowDownLeft className="mr-2 size-4 text-emerald-600" />
              Add a deposit
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => openCreate('withdrawal')}>
              <ArrowUpRight className="mr-2 size-4 text-[#062B1A]/60" />
              Add a withdrawal
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );

  return (
    <AccountingShell
      title="Transactions"
      description="Every movement of money the college has recorded. Payments the portal already knows about — tuition, bills, commission cheques — appear here on their own; add the ones it does not, and categorise the lot."
      actions={actions}
    >
      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Money in
              </p>
              <p className="mt-2 text-2xl font-semibold text-emerald-700">
                {summary.money_in_display}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Money out
              </p>
              <p className="mt-2 text-2xl font-semibold text-[#062B1A]">
                {summary.money_out_display}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Net
              </p>
              <p
                className={cn(
                  'mt-2 text-2xl font-semibold',
                  summary.net_cents < 0 ? 'text-red-700' : 'text-[#062B1A]'
                )}
              >
                {summary.net_display}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Not yet reviewed
              </p>
              <p className="mt-2 text-2xl font-semibold text-[#062B1A]">
                {summary.unreviewed_count}
              </p>
              {summary.unreviewed_count > 0 && (
                <button
                  type="button"
                  onClick={() => setFilter({ review: 'unreviewed' })}
                  className="mt-1 text-[11px] font-medium text-[#062B1A]/50 underline-offset-2 hover:underline"
                >
                  Show them
                </button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filters.account}
          onChange={(event) => setFilter({ account: event.target.value })}
          aria-label="Account"
          className={cn(
            'h-9 rounded-md border bg-white px-3 text-sm font-medium text-[#062B1A]',
            adminInputClass
          )}
        >
          <option value="">All accounts</option>
          {(chart?.payment_accounts || []).map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>

        <div className="min-w-[200px] flex-1">
          <Input
            type="search"
            value={filters.q}
            onChange={(event) => setFilter({ q: event.target.value })}
            placeholder="Search descriptions, people, categories, amounts"
            aria-label="Search transactions"
            className={cn('h-9', adminInputClass)}
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowFilters((open) => !open)}
          className={cn(filterPillClass, filterCount > 0 && filterPillActiveClass)}
        >
          <Filter className="mr-1.5 size-3.5" />
          Filters{filterCount > 0 ? ` (${filterCount})` : ''}
        </Button>

        <select
          value={filters.sort}
          onChange={(event) => setFilter({ sort: event.target.value })}
          aria-label="Sort transactions"
          className={cn('h-9 rounded-md border bg-white px-3 text-sm', adminInputClass)}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {filterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ ...DEFAULT_FILTERS, account: filters.account })}
            className="h-8 px-2 text-xs text-[#062B1A]/60"
          >
            <X className="mr-1 size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {showFilters && (
        <TableFilterCard>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label htmlFor="txn-filter-type">Type</Label>
              <select
                id="txn-filter-type"
                value={filters.type}
                onChange={(event) => setFilter({ type: event.target.value })}
                className={cn(
                  'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                  adminInputClass
                )}
              >
                <option value="all">Deposits and withdrawals</option>
                <option value="deposit">Deposits only</option>
                <option value="withdrawal">Withdrawals only</option>
              </select>
            </div>
            <div>
              <Label htmlFor="txn-filter-category">Category</Label>
              <select
                id="txn-filter-category"
                value={filters.category}
                onChange={(event) => setFilter({ category: event.target.value })}
                className={cn(
                  'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                  adminInputClass
                )}
              >
                <option value="">Any category</option>
                {categoryOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.group} · {account.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="txn-filter-review">Review</Label>
              <select
                id="txn-filter-review"
                value={filters.review}
                onChange={(event) => setFilter({ review: event.target.value })}
                className={cn(
                  'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                  adminInputClass
                )}
              >
                <option value="all">Reviewed and not</option>
                <option value="unreviewed">Not yet reviewed</option>
                <option value="reviewed">Reviewed</option>
              </select>
            </div>
            <div>
              <Label htmlFor="txn-filter-source">Where it came from</Label>
              <select
                id="txn-filter-source"
                value={filters.source}
                onChange={(event) => setFilter({ source: event.target.value })}
                className={cn(
                  'mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm',
                  adminInputClass
                )}
              >
                <option value="all">Anywhere</option>
                <option value="system">Recorded by the portal</option>
                <option value="manual">Entered by hand</option>
              </select>
            </div>
            <div>
              <Label htmlFor="txn-filter-from">Date from</Label>
              <Input
                id="txn-filter-from"
                type="date"
                value={filters.from}
                onChange={(event) => setFilter({ from: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="txn-filter-to">Date to</Label>
              <Input
                id="txn-filter-to"
                type="date"
                value={filters.to}
                onChange={(event) => setFilter({ to: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
          </div>
        </TableFilterCard>
      )}

      {canDelete && (
        <BulkDeleteBar
          count={selection.count}
          noun="transaction"
          onDelete={() => setBulkOpen(true)}
          onClear={selection.clear}
        />
      )}

      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading ? (
        <TableSkeleton rows={8} />
      ) : rows.length === 0 ? (
        <AccountingEmpty
          title={filterCount > 0 ? 'No transactions match these filters' : 'No transactions yet'}
          description={
            filterCount > 0
              ? 'Clear a filter to widen the search.'
              : 'Payments recorded elsewhere in the portal appear here on their own. Add a deposit or a withdrawal for money that moved some other way.'
          }
          action={
            filterCount > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFilters({ ...DEFAULT_FILTERS, account: filters.account })}
              >
                Clear filters
              </Button>
            ) : canCreate ? (
              <Button
                type="button"
                onClick={() => openCreate('withdrawal')}
                className="bg-[#062B1A] text-white"
              >
                <Plus className="mr-2 size-4" />
                Add a transaction
              </Button>
            ) : null
          }
        />
      ) : (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[1230px] text-left text-sm">
              <thead className="bg-[#062B1A] text-[10px] uppercase tracking-[0.035em] text-white">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        aria-label="Select every transaction on this page that can be deleted"
                        className="border-white/50 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-[#062B1A]"
                        checked={selection.allSelected}
                        disabled={selection.selectableCount === 0}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                  )}
                  <th className="w-10 px-3 py-2.5 font-semibold">
                    <span className="sr-only">Reviewed</span>
                  </th>
                  <th className="px-3 py-2.5 font-semibold">Date</th>
                  <th className="px-3 py-2.5 font-semibold">Description</th>
                  <th className="px-3 py-2.5 font-semibold">Account</th>
                  <th className="px-3 py-2.5 font-semibold">Category</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                {rows.map((row) => {
                  const mode = entryModeMeta(row.entry_mode);
                  const busy = busyRef === row.ref;
                  const deposit = row.direction === 'deposit';
                  return (
                    <tr key={row.ref} className="bg-white align-top hover:bg-[#F7F8FA]/80">
                      {canDelete && (
                        <td className="px-3 py-3">
                          <Checkbox
                            aria-label={`Select ${row.description || 'this transaction'}`}
                            checked={selection.isSelected(row)}
                            disabled={!row.delete_effect?.allowed}
                            title={row.delete_effect?.reason || undefined}
                            onCheckedChange={() => selection.toggle(row)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          disabled={!canReview || busy}
                          onClick={() => toggleReview(row)}
                          aria-pressed={row.reviewed}
                          title={
                            row.reviewed
                              ? `Reviewed${row.reviewed_by_name ? ` by ${row.reviewed_by_name}` : ''}. Click to undo.`
                              : 'Mark this transaction as reviewed'
                          }
                          className={cn(
                            'flex size-7 items-center justify-center rounded-full border transition-colors',
                            row.reviewed
                              ? 'border-emerald-300 bg-emerald-500 text-white hover:bg-emerald-600'
                              : 'border-[#062B1A]/15 text-[#062B1A]/25 hover:border-emerald-300 hover:text-emerald-600',
                            (!canReview || busy) && 'cursor-not-allowed opacity-60'
                          )}
                        >
                          {busy ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Check className="size-3.5" />
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-[#062B1A]/70">
                        {row.date || '—'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {deposit ? (
                            <ArrowDownLeft className="size-3.5 shrink-0 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="size-3.5 shrink-0 text-[#062B1A]/40" />
                          )}
                          <span className="font-medium text-[#062B1A]">
                            {row.description || '—'}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-[#062B1A]/45">
                          <StatusPill tone={mode.tone} className="px-1.5 py-0 text-[10px]">
                            {row.entry_mode === 'system' ? row.source.label : mode.label}
                          </StatusPill>
                          {row.customer?.name && <span>{row.customer.name}</span>}
                          {row.payee?.name && <span>{row.payee.name}</span>}
                          {row.notes && <span className="truncate">· {row.notes}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-[#062B1A]/70">{row.account?.name}</td>
                      <td className="px-3 py-3">
                        {row.split_count > 1 || row.amount_cents === 0 ? (
                          /* Kept box-shaped so the column stays a column, but
                             dashed and inert: a split cannot be collapsed into
                             one category by picking from a list, and a record
                             at zero has nothing to file. */
                          <span
                            className="flex h-8 min-w-[9rem] max-w-[15rem] items-center truncate rounded-md border border-dashed border-[#062B1A]/20 bg-[#F7F8FA] px-2 text-xs text-[#062B1A]/70"
                            title={
                              row.split_count > 1
                                ? 'Split across several categories — open Edit more details to change it'
                                : 'This record moved no money, so there is nothing to categorise'
                            }
                          >
                            {row.category_summary}
                          </span>
                        ) : (
                          <CategoryPicker
                            variant="cell"
                            portal
                            emphasis={row.category_assigned ? 'normal' : 'muted'}
                            value={row.splits?.[0]?.category_id}
                            onChange={(categoryId) => quickCategorise(row, categoryId)}
                            groups={chart?.groups || []}
                            accountsById={chart?.byId || {}}
                            accountTypes={chart?.account_types || []}
                            canCreate={Boolean(permissions.manage_ledger_accounts)}
                            onCreate={createCategory}
                            excludeId={row.account_id}
                            disabled={!canEdit || busy}
                            placeholder="Uncategorised"
                          />
                        )}
                        <div className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-[#062B1A]/40">
                          {!row.category_assigned && <span>suggested</span>}
                          {row.tax_total_cents > 0 && (
                            <span>includes {row.tax_total_display} tax</span>
                          )}
                        </div>
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap px-3 py-3 text-right text-sm font-semibold',
                          deposit ? 'text-emerald-700' : 'text-[#062B1A]'
                        )}
                      >
                        {deposit ? '+' : '−'}
                        {row.amount_display}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="size-8 rounded-full p-0"
                                aria-label={`Actions for ${row.description || 'this transaction'}`}
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              {/* A voided cheque is listed at zero so the
                                  history stays complete. There is nothing to
                                  file against it, so editing is not offered. */}
                              {canEdit && (row.editable || row.amount_cents > 0) && (
                                <DropdownMenuItem onSelect={() => openEdit(row)}>
                                  <Pencil className="mr-2 size-4" />
                                  Edit more details
                                </DropdownMenuItem>
                              )}
                              {row.source?.path && (
                                <DropdownMenuItem asChild>
                                  <Link to={row.source.path}>
                                    <ExternalLink className="mr-2 size-4" />
                                    Open {row.source.label.toLowerCase()}
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    disabled={!row.delete_effect?.allowed}
                                    onSelect={() => setDeleteFor(row)}
                                    className="text-red-700 focus:text-red-700"
                                  >
                                    <Trash2 className="mr-2 size-4" />
                                    Delete
                                  </DropdownMenuItem>
                                  {/* Saying why beats a greyed-out row with no
                                      explanation, which just reads as broken.
                                      The sentence is the server's, because only
                                      it knows the reason. */}
                                  {!row.delete_effect?.allowed && row.delete_effect?.reason && (
                                    <p className="max-w-[15rem] px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-[#062B1A]/45">
                                      {row.delete_effect.reason}
                                    </p>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageInfo && pageInfo.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgba(6,43,26,0.07)] bg-white px-4 py-3 text-xs text-[#062B1A]/60">
              <span>{pageInfo.total_count} transactions</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page <= 1}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      page: String(pageInfo.current_page - 1),
                    }))
                  }
                >
                  Previous
                </Button>
                <span>
                  Page {pageInfo.current_page} of {pageInfo.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page >= pageInfo.total_pages}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      page: String(pageInfo.current_page + 1),
                    }))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        transaction={editing}
        direction={newDirection}
        chart={chart}
        taxes={taxes}
        payees={payees}
        canCreateCategory={Boolean(permissions.manage_ledger_accounts)}
        onCreateCategory={createCategory}
        onSubmit={submitDialog}
      />

      <BulkDeleteDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        noun="transaction"
        rows={selection.selectedRows}
        labelOf={(row) => `${row.date} · ${row.description} · ${row.amount_display}`}
        cascadeOf={(row) => row.delete_effect?.cascade}
        remove={(row) => deleteAccountingTransaction(row.ref)}
        onFinished={() => {
          selection.clear();
          load({ quiet: true });
        }}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteFor)}
        onOpenChange={(next) => !next && setDeleteFor(null)}
        title="Delete this transaction?"
        detail={
          deleteFor
            ? `${deleteFor.date} · ${deleteFor.description} · ${deleteFor.amount_display}`
            : ''
        }
        consequence={deleteFor?.delete_effect?.cascade || ''}
        confirmLabel="Delete transaction"
        busy={Boolean(busyRef)}
        onConfirm={confirmDelete}
      />
    </AccountingShell>
  );
}
